import { Button, Card, Col, Modal, Row, Table, TableColumnsType, Typography } from 'antd';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ActionButton from '../../../../components/ActionButton';
import BoldButtonLabel from '../../../../components/BoldButtonLabel';
import FieldDisplay from '../../../../components/FieldDisplay';
import TenantInfo from '../../../../components/TenantIdInfo';
import { convertQueryStringToObj } from '../../../../helpers';
import { formatDateTime } from '../../../../helpers/date.helpers';
import _ from '../../../../helpers/lodash';
import { displayErrorNotifications } from '../../../../helpers/toast.helpers';
import PrimaryLayout from '../../../../layouts/primary-layout';
import { useLoader } from '../../../../stores/use-loader';
import { loyaltyService } from '../../services/loyalty.service';
import { IRewardDetailItem, IRewardDetailResponse, IRewardIdDetail } from '../../types/reward-history';

interface IRewardHistoryDetailProps {}

const RewardHistoryDetail: React.FunctionComponent<IRewardHistoryDetailProps> = props => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { setLoading } = useLoader(({ loading, setLoading }) => ({ loading, setLoading }));
  const { t } = useTranslation();

  const [rewardDetail, setRewardDetail] = React.useState({} as IRewardDetailResponse);
  const queryString = searchParams.toString();
  const queryStringObj = convertQueryStringToObj(queryString);
  const { customer_id, reward_name, trigger_type, applicability_type, status, channel, site, date_from, date_to } =
    queryStringObj;

  React.useEffect(() => {
    const offset = parseInt(searchParams.get('offset') || '0');
    const limit = parseInt(searchParams.get('limit') || '10');
    loadInitialData(offset, limit);
  }, [searchParams]);

  const loadInitialData = async (offset = 0, limit = 10) => {
    setLoading(true);
    const params = {
      'reward-name': reward_name,
      trigger_type,
      applicability_type,
      status,
      channel,
      site,
      date_from,
      date_to,
      offset,
      limit
    };
    const { data, errors } = await loyaltyService.getRewardHistoryDetails(customer_id, params);
    if (_.isEmpty(errors)) {
      setRewardDetail(data);
    } else {
      displayErrorNotifications(errors);
    }
    setLoading(false);
  };

  const columns: TableColumnsType<IRewardDetailItem> = [
    {
      title: t('rewardValue'),
      render(value, record, index) {
        return <>{record.reward_value}</>;
      },
      align: 'center'
    },
    {
      title: t('redeemableValue'),
      render(value, record, index) {
        return <>{record?.redeemable_value.toFixed(2)}</>;
      },
      align: 'center'
    },

    {
      title: t('channel'),
      render(value, record, index) {
        let channelType = '';

        if (record.channel === 'Online') channelType = 'online';
        else if (record.channel === 'Store') channelType = 'store';
        else if (record.channel === 'Marketplace') channelType = 'marketPlace';

        return <>{t(channelType)}</>;
      },
      align: 'center'
    },

    {
      title: t('site'),
      render(value, record, index) {
        return <>{record.site_id}</>;
      },
      align: 'center'
    },

    {
      title: t('status'),
      render(value, record, index) {
        return <>{record.status === 'ON_HOLD' ? t('onHold') : t((record.status || '')?.toLowerCase())}</>;
      },
      align: 'center'
    },

    {
      title: t('rewardDate'),
      render(value, record, index) {
        return <>{formatDateTime(record.created_date_time)}</>;
      },
      align: 'center'
    },

    {
      title: t('loyaltyType'),
      render(value, record, index) {
        let type = '';

        if (record.loyality_type === 'Action Oriented') type = 'actionOriented';
        else if (record.loyality_type === 'Earn & Burn') type = 'earnAndBurn';
        return <>{t(type)}</>;
      },
      align: 'center'
    },
    {
      title: t('applicabilityType'),
      render(value, record, index) {
        let type = '';

        if (record.applicability_type === 'Sign up') {
          type = 'signUp';
        } else if (record.applicability_type === 'Order') {
          type = 'order';
        } else if (record.applicability_type === 'Referral') {
          type = 'referral';
        } else if (record.applicability_type === 'Survey') {
          type = 'survey';
        } else if (record.applicability_type === 'Review') {
          type = 'review';
        } else if (record.applicability_type === 'Sale') {
          type = 'sale';
        } else if (record.applicability_type === 'Abandon Cart') {
          type = 'abandonCart';
        } else if (record.applicability_type === 'Inactive Buyer') {
          type = 'inactiveBuyer';
        } else if (record.applicability_type === 'Scheduled') {
          type = 'scheduled';
        } else if (record.applicability_type === 'Return') {
          type = 'return';
        }
        // Add more conditions as needed

        return <>{t(type) || record?.applicability_type}</>;
      },
      align: 'center'
    },

    {
      title: t('transactionType'),
      render(value, record, index) {
        let type = '';

        if (record.transaction_type === 'Credit') type = 'credit';
        else if (record.transaction_type === 'Debit') type = 'debit';

        return <>{t(type)}</>;
      },
      align: 'center'
    },

    {
      title: t('expireDate'),
      render(value, record, index) {
        return <>{formatDateTime(record.expire_by)}</>;
      },
      align: 'center'
    },

    {
      title: <></>,
      render(value, record, index) {
        return (
          <ActionButton
            action="VIEW"
            onClick={() => {
              handleClickRewardDetail(record.id);
            }}
            title={t('rewardDetails')}
          />
        );
      },
      align: 'center'
    }
  ];

  const handleClickRewardDetail = async (rewardId: string) => {
    setLoading(true);

    if (!rewardIdDetailMap[rewardId]) {
      const { data, errors } = await loyaltyService.getRewardIdDetails(rewardId);
      setRewardIdMap(rewardIdMap => ({ ...rewardIdMap, [rewardId]: data }));
      if (!_.isEmpty(errors)) {
        displayErrorNotifications(errors);
        setLoading(false);
        return;
      }
    }

    setSelectedId(rewardId);
    setLoading(false);
  };
  const handlePageChange = (currentPage: number) => {
    loadInitialData(currentPage - 1);
  };

  const [selectedId, setSelectedId] = React.useState<string>('');
  const [rewardIdDetailMap, setRewardIdMap] = React.useState<Record<string, IRewardIdDetail>>({});

  const isModalVisible = !_.isEmpty(rewardIdDetailMap[selectedId]);
  const rewardIdDetail = rewardIdDetailMap[selectedId];
  const navigate = useNavigate();
  const handleBack = () => {
    if (searchParams.get('backUri')) {
      navigate(searchParams.get('backUri') as string);
    } else {
      navigate('/loyalty/reports');
    }
  };

  return (
    <PrimaryLayout>
      <div className="container mx-auto px-4">
        <Card>
          <Row justify={'space-between'} className="mb-4">
            <Col>
              <Typography.Title level={3} className="text-[#2e2a5b]">
                {t('customerRewardHistoryDetail')}
              </Typography.Title>
            </Col>
          </Row>
          <TenantInfo />
          <section className="flex gap-8 my-4 flex-wrap">
            <FieldDisplay label={t('customer')} value={customer_id} />
            <FieldDisplay label={t('rewardName')} value={reward_name} />
          </section>
          <section className="mt-8">
            <Table
              pagination={{
                total: rewardDetail?.page_info?.total_pages
                  ? rewardDetail.page_info?.total_pages * parseInt(searchParams.get('limit') as string) || 10
                  : 0,
                showSizeChanger: true,
                pageSizeOptions: ['1', '10', '20', '50', '100'],
                pageSize: parseInt(searchParams.get('limit') as string) || 10,
                current: parseInt(searchParams.get('offset') as string) + 1 || 1,
                onChange: (currentPage, pageSize) => {
                  setSearchParams(prev => {
                    prev.set('offset', currentPage - 1 + '');
                    prev.set('limit', pageSize + '');
                    return prev;
                  });
                },
                locale: {
                  items_per_page: `${t('page')}`
                }
              }}
              locale={{
                emptyText: t('noData')
              }}
              bordered
              dataSource={rewardDetail.data}
              columns={columns}
              scroll={{ x: 1000 }}
            ></Table>
          </section>{' '}
          <Button size="large" className="mt-4" type="primary" onClick={handleBack}>
            <BoldButtonLabel labelText={t('goBack')} />
          </Button>
        </Card>
      </div>
      <Modal
        open={isModalVisible}
        footer={null}
        width={700}
        centered
        onCancel={() => {
          setSelectedId('');
        }}
      >
        <Typography.Title level={4}>{t('rewardDetails')}</Typography.Title>
        <section className="flex flex-col flex-wrap gap-4">
          <FieldDisplay label={t('customerId')} value={rewardIdDetail?.customer_id} />
          <FieldDisplay label={t('loyaltyType')} value={t(rewardIdDetail?.loyality_type)} />
          <FieldDisplay label={t('applicabilityType')} value={t(rewardIdDetail?.applicability_type)} />
          <FieldDisplay label={t('rewardName')} value={rewardIdDetail?.reward_name} />
          <FieldDisplay label={t('rewardType')} value={t((rewardIdDetail?.reward_type || '').toLowerCase())} />
          <FieldDisplay label={t('rewardValue')} value={rewardIdDetail?.reward_value} />
          <FieldDisplay label={t('maxUse')} value={rewardIdDetail?.max_use} />
          <FieldDisplay label={t('channel')} value={t(rewardIdDetail?.channel)} />
          <FieldDisplay label={t('pointConversionFactor')} value={rewardIdDetail?.point_conversion_factor} />
          <FieldDisplay label={t('redeemableValue')} value={rewardIdDetail?.redeemable_value} />
          <FieldDisplay
            label={t('status')}
            value={rewardIdDetail?.status === 'ON_HOLD' ? t('onHold') : t((rewardIdDetail?.status || '').toLowerCase())}
          />
          <FieldDisplay
            label={t('transactionType')}
            value={t((rewardIdDetail?.transaction_type || '').toLowerCase())}
          />
        </section>
      </Modal>
    </PrimaryLayout>
  );
};

export default RewardHistoryDetail;
